/**
 * Firestore Collection References
 *
 * Centralized typed collection references for all Firestore collections.
 * This ensures type safety when querying and writing to Firestore.
 */

import { collection, CollectionReference } from 'firebase/firestore';
import { db } from './firestore';
import type {
  Space,
  SpaceMember,
  SpaceInvitation,
  EventRSVP,
  ActivityLog,
  UserProfile,
} from '../types/firestore';

/**
 * Spaces Collection
 * Stores shared space metadata and settings
 */
export const spacesCollection = collection(
  db,
  'spaces'
) as CollectionReference<Space>;

/**
 * Space Members Collection
 * Stores user membership in spaces with roles and permissions
 */
export const membersCollection = collection(
  db,
  'space_members'
) as CollectionReference<SpaceMember>;

/**
 * Space Invitations Collection
 * Stores pending and historical invitations to spaces
 */
export const invitationsCollection = collection(
  db,
  'space_invitations'
) as CollectionReference<SpaceInvitation>;

/**
 * Event RSVPs Collection
 * Stores RSVP responses for events in shared spaces
 */
export const rsvpsCollection = collection(
  db,
  'event_rsvps'
) as CollectionReference<EventRSVP>;

/**
 * Activity Logs Collection
 * Stores activity history for activity feeds (optional)
 */
export const activityLogsCollection = collection(
  db,
  'activity_logs'
) as CollectionReference<ActivityLog>;

/**
 * User Profiles Collection
 * Stores extended user information beyond Firebase Auth
 */
export const userProfilesCollection = collection(
  db,
  'user_profiles'
) as CollectionReference<UserProfile>;

/**
 * Helper function to get a typed collection reference
 * Useful for dynamic collection access
 */
export function getCollection<T>(collectionName: string): CollectionReference<T> {
  return collection(db, collectionName) as CollectionReference<T>;
}
